import { useEffect, useRef } from 'react';
import { Button, Center, Column, Row, Spinner, Typography } from '@tempmee/react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router';
import { Divider } from '~/commonComponents';
import { EnforcePostAJobFunnelImprovements } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { firePermPostJobWorkDetailsSubmitted } from '~/posthogs/events';
import {
  AutoSaveStatus,
  EarningBenefitForm,
  GeneralErrorMessage,
  InfoSection,
  JobDetailsFormMapper,
  useJobDetailsForm,
} from '../JobDetailsForm';
import { ScheduleForm } from '../JobDetailsForm/components/ScheduleInfo';
import {
  CreatePermanentJobPostingFormSection,
  CreatePermanentJobPostingSteps,
  TCreatePermanentJobPostingPageRoutes,
} from './Types';
import {
  useAutoSaveDraftJobPost,
  useAutoScrollToSection,
  useDraftJobPost,
  useSaveJobPost,
} from './hooks';

export const WorkDetailsPage = () => {
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();
  const { draft, isLoading } = useDraftJobPost(id);
  const isEnforcePostAJobFunnelImprovement = useFeatureFlag(EnforcePostAJobFunnelImprovements);

  const scheduleRef = useRef<HTMLDivElement>(null);
  const earningsAndBenefitsRef = useRef<HTMLDivElement>(null);

  const controller = useJobDetailsForm({
    initialValues: draft,
  });

  useEffect(() => {
    if (draft) {
      controller.reset(JobDetailsFormMapper.fromEntity(draft));
    }
  }, [draft, controller]);

  useAutoScrollToSection({
    sectionRefs: {
      [CreatePermanentJobPostingFormSection.Schedule]: scheduleRef,
      [CreatePermanentJobPostingFormSection.EarningsAndBenefits]: earningsAndBenefitsRef,
    },
    isReady: !isLoading && !!draft,
  });

  const { isPending, hasFormErrors, onSave } = useSaveJobPost(controller, id, {
    targetStep: CreatePermanentJobPostingSteps.JobDescription,
    triggers: isEnforcePostAJobFunnelImprovement
      ? [
          'benefits',
          'benefitsOther',
          'compensationType',
          'expectedDays',
          'minRate',
          'payType',
          'scheduleType',
        ]
      : [
          'benefits',
          'benefitsOther',
          'compensationType',
          'expectedDays',
          'maxRate',
          'minRate',
          'payType',
          'scheduleType',
        ],
    onTrackSuccess: (postingId, formData) => {
      firePermPostJobWorkDetailsSubmitted({
        postingId,
        professionType: formData.professionType,
      });
    },
  });
  const {
    isLoading: isAutoSaveLoading,
    isShowSavedMessage,
    lastSavedAt,
  } = useAutoSaveDraftJobPost(controller, id);

  if (isLoading) {
    return (
      <Center style={{ marginBlock: 48 }}>
        <Spinner centered variant="default" size="md" />
      </Center>
    );
  }

  if (isEnforcePostAJobFunnelImprovement) {
    return (
      <>
        <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />
        <Column align="center" gap={4}>
          {hasFormErrors && <GeneralErrorMessage />}
          <FormProvider {...controller}>
            <Column width="100%" className="self-center" style={{ maxWidth: 800 }}>
              <InfoSection
                ref={scheduleRef}
                title="Schedule & Pay"
                titleFontSize={24}
                titleColor="neutral_600"
              >
                <Column gap={6}>
                  <ScheduleForm control={controller.control} errors={controller.formState.errors} />
                  <Divider height={1} marginVertical={0} />
                  <EarningBenefitForm />
                </Column>
              </InfoSection>
            </Column>
          </FormProvider>
          <Row justify="center" className="mt-6">
            <Button onClick={onSave} isLoading={isPending || isAutoSaveLoading} size="lg">
              Continue
            </Button>
          </Row>
        </Column>
      </>
    );
  }

  return (
    <>
      <AutoSaveStatus visible={isShowSavedMessage} date={lastSavedAt} />
      <Column width="60%" className="self-center">
        <Typography as="span" bold size="heading_4" style={{ marginBottom: 14, marginLeft: 24 }}>
          Step 2: Work Details
        </Typography>
        {hasFormErrors && <GeneralErrorMessage />}
        <FormProvider {...controller}>
          <Column gap={4}>
            <InfoSection ref={scheduleRef} icon="calendar-solid" title="Schedule">
              <ScheduleForm control={controller.control} errors={controller.formState.errors} />
            </InfoSection>
            <InfoSection
              ref={earningsAndBenefitsRef}
              icon="money-pay-solid"
              title="Earnings and Benefits"
            >
              <EarningBenefitForm />
            </InfoSection>
          </Column>
        </FormProvider>
        <Row justify="center" className="mt-6">
          <Button onClick={onSave} isLoading={isPending || isAutoSaveLoading} size="lg">
            Next
          </Button>
        </Row>
      </Column>
    </>
  );
};
